import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { formatTimestampOr } from "../lib/formatTime.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { STATUS_LABELS, STATUS_PILL_CLASSES, type ConnectorStatus } from "../lib/connectorVocab.js";

type ConnectorRow = {
  id: string;
  name: string;
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  last_heartbeat_at: string | null;
  /**
   * ADR-0164 §5.2 — the hub's derived status. This row used to carry a
   * `healthy` boolean; the hub renamed it to `agent_reachable` and this page
   * was the one surface not migrated, so it read `undefined` and called every
   * connector offline. Render what the hub derives, never a local derivation.
   */
  status: ConnectorStatus;
  version: string;
  protocol: string;
};

export function AdminStatus() {
  const [connectors, setConnectors] = useState<ConnectorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh(opts?: { silent?: boolean }) {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ connectors: ConnectorRow[] }>("/api/admin/tenants/status");
      setConnectors(data.connectors);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    let last = 0;
    function maybeRefresh() {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - last < 15_000) return;
      last = now;
      void refresh({ silent: true });
    }
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", maybeRefresh);
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", maybeRefresh);
    };
  }, []);

  return (
    <div>
      <PageLayout title="System health">
        {error && <ErrorState detail={error} />}
        {loading && <LoadingState />}

        {!loading && (
          <div className="card tableWrap">
            <table className="table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Slug</th>
                <th>Connector name</th>
                <th>Last heartbeat</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {connectors.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div style={{ fontWeight: 800 }}>{c.tenant_name}</div>
                  </td>
                  <td className="muted">{c.tenant_slug}</td>
                  <td>
                    <Link to={`/admin/tenants/${c.tenant_id}#connectors`} style={{ textDecoration: "underline" }}>
                      {c.name}
                    </Link>
                  </td>
                  <td className="muted">{formatTimestampOr(c.last_heartbeat_at, "Never")}</td>
                  <td>
                    <span className={STATUS_PILL_CLASSES[c.status]}>{STATUS_LABELS[c.status]}</span>
                  </td>
                </tr>
              ))}
              {connectors.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    No connectors yet.
                  </td>
                </tr>
              )}
            </tbody>
            </table>
          </div>
        )}
      </PageLayout>
    </div>
  );
}

